import React, { useState } from "react";
import { Input } from "@/ui-components/ui/input";
import { Label } from "@/ui-components/ui/label";
import { UserPlus2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { useSponsorsUpdate } from "@/lib/api/requests/sponsors.requests";
import { ContactPersonResponseDto } from "@/lib/api/interfaces/sponsors.interface";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";

interface AddContactDialogProps {
  sponsorId: string;
  existingContacts: ContactPersonResponseDto[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const AddContactDialog: React.FC<AddContactDialogProps> = ({
  sponsorId,
  existingContacts,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateSponsorMutation = useSponsorsUpdate();

  const handleClose = () => {
    onOpenChange(false);
    setName("");
    setEmail("");
    setPhone("");
    setRole("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Name erforderlich", {
        description: "Bitte gib einen Namen für den Kontakt ein.",
      });
      return;
    }

    setIsSubmitting(true);

    const newContact = {
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      role: role.trim() || undefined,
    };

    const updatedContacts = [
        newContact,
        ...existingContacts.map(c => ({
            name: c.name,
            email: c.email,
            phone: c.phone,
            mobile: c.mobile,
            role: c.role,
            avatar: c.avatar
        }))
    ];

    try {
      await updateSponsorMutation.mutateAsync({
        id: sponsorId,
        data: { contacts: updatedContacts } as any,
      });

      toast.success("Kontakt hinzugefügt", {
        description: "Der neue Ansprechpartner wurde erfolgreich gespeichert.",
      });

      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      toast.error("Fehler beim Speichern", {
        description: "Der Kontakt konnte nicht hinzugefügt werden.",
        error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CreateBaseDialog
      open={open}
      onClose={handleClose}
      icon={UserPlus2}
      title="Kontakt hinzufügen"
      description="Füge einen neuen Ansprechpartner für diesen Sponsor hinzu."
      isSaving={isSubmitting}
      canSave={name.trim().length > 0}
      formId="add-contact-form"
      submitLabel="Speichern"
      submitLoadingLabel="Speichert..."
      maxWidth="lg"
    >
      <form id="add-contact-form" onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Vor- und Nachname"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-role">Rolle / Funktion</Label>
              <Input
                id="contact-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="z.B. Marketingleiter"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">E-Mail</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@beispiel.de"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Telefon</Label>
              <Input
                id="contact-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+49 123 456789"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
          </div>
        </div>
      </form>
    </CreateBaseDialog>
  );
};
